import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BUILT_IN_FUND_TYPES,
  encodeFundTypeValue,
} from '@/components/shared/fund-type-select';
import {
  DateRangeFilter,
  INSIGHTS_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { dataTableMoneyColumnTotals } from '@/lib/money-totals';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatNumber } from '@/utils/format';

import { GivingSubnav } from './giving-subnav';

interface FundCustomType {
  id: string;
  name: string;
  code: string;
}

interface FundRow {
  id: string;
  fundType: string;
  customTypeId: string | null;
  isActive: boolean;
}

interface FundInsights {
  currency: string;
  byFund: Array<{
    fundId: string;
    currency: string;
    amountMinor: number;
    count: number;
  }>;
}

interface FundTypeTableRow {
  id: string;
  name: string;
  code: string | null;
  source: 'built_in' | 'custom';
  fundCount: number;
  amountMinor: number;
  giftCount: number;
  currency: string;
}

const SourceBadge = ({
  source,
  builtInLabel,
  customLabel,
}: {
  source: FundTypeTableRow['source'];
  builtInLabel: string;
  customLabel: string;
}) => (
  <Badge variant={source === 'built_in' ? 'neutral' : 'brand'}>
    {source === 'built_in' ? builtInLabel : customLabel}
  </Badge>
);

const fetchAllFunds = async (): Promise<FundRow[]> => {
  const rows: FundRow[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await api.get<{ data: FundRow[]; page: { totalPages: number } }>(
      '/giving/funds',
      { query: { page, pageSize: 100, sort: 'name' } },
    );
    rows.push(...response.data);
    totalPages = response.page.totalPages;
    page += 1;
  } while (page <= totalPages);

  return rows;
};

export const FundTypesPage = () => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';
  const baseCurrency = church?.baseCurrency ?? 'GHS';
  const canManage = hasPermission(membership, PERMISSIONS.GIVING_MANAGE_FUNDS);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FundCustomType | null>(null);
  const [name, setName] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));

  const customTypes = useQuery({
    queryKey: ['giving', 'fund-custom-types'],
    queryFn: () =>
      api.get<{ data: FundCustomType[] }>('/giving/fund-custom-types?pageSize=100'),
  });

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'all-for-types'],
    queryFn: fetchAllFunds,
  });

  const insights = useQuery({
    queryKey: ['giving', 'fund-insights', dateRange.from, dateRange.to],
    queryFn: () =>
      api.get<FundInsights>('/giving/funds/insights', {
        query: {
          dateFrom: dateRange.from ?? undefined,
          dateTo: dateRange.to ?? undefined,
        },
      }),
  });

  const rows = useMemo((): FundTypeTableRow[] => {
    const fundTypeByFundId = new Map(
      (funds.data ?? []).map((fund) => [
        fund.id,
        encodeFundTypeValue(fund.fundType, fund.customTypeId),
      ]),
    );

    const stats = new Map<
      string,
      { fundCount: number; amountMinor: number; giftCount: number; currency: string }
    >();

    for (const fund of funds.data ?? []) {
      if (!fund.isActive) continue;
      const key = encodeFundTypeValue(fund.fundType, fund.customTypeId);
      const current = stats.get(key) ?? {
        fundCount: 0,
        amountMinor: 0,
        giftCount: 0,
        currency: insights.data?.currency ?? baseCurrency,
      };
      current.fundCount += 1;
      stats.set(key, current);
    }

    for (const gift of insights.data?.byFund ?? []) {
      const key = fundTypeByFundId.get(gift.fundId);
      if (!key) continue;
      const current = stats.get(key) ?? {
        fundCount: 0,
        amountMinor: 0,
        giftCount: 0,
        currency: gift.currency,
      };
      current.amountMinor += gift.amountMinor;
      current.giftCount += gift.count;
      current.currency = gift.currency;
      stats.set(key, current);
    }

    const builtInRows: FundTypeTableRow[] = BUILT_IN_FUND_TYPES.map((fundType) => {
      const typeStats = stats.get(fundType);
      return {
        id: fundType,
        name: t(`fundTypes.${fundType}`),
        code: null,
        source: 'built_in',
        fundCount: typeStats?.fundCount ?? 0,
        amountMinor: typeStats?.amountMinor ?? 0,
        giftCount: typeStats?.giftCount ?? 0,
        currency: typeStats?.currency ?? baseCurrency,
      };
    });

    const customRows: FundTypeTableRow[] = (customTypes.data?.data ?? []).map((type) => {
      const key = encodeFundTypeValue('custom', type.id);
      const typeStats = stats.get(key);
      return {
        id: type.id,
        name: type.name,
        code: type.code,
        source: 'custom',
        fundCount: typeStats?.fundCount ?? 0,
        amountMinor: typeStats?.amountMinor ?? 0,
        giftCount: typeStats?.giftCount ?? 0,
        currency: typeStats?.currency ?? baseCurrency,
      };
    });

    return [...builtInRows, ...customRows].sort((left, right) => {
      if (left.source !== right.source) {
        return left.source === 'built_in' ? -1 : 1;
      }
      if (right.fundCount !== left.fundCount) {
        return right.fundCount - left.fundCount;
      }
      return left.name.localeCompare(right.name);
    });
  }, [baseCurrency, customTypes.data?.data, funds.data, insights.data, t]);

  const tableTotals = useMemo(
    (): { amountMinor: number; currency: string; count: number } => ({
      amountMinor: rows.reduce((sum, row) => sum + row.amountMinor, 0),
      currency: insights.data?.currency ?? baseCurrency,
      count: rows.reduce((sum, row) => sum + row.giftCount, 0),
    }),
    [baseCurrency, insights.data?.currency, rows],
  );

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setName('');
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDialogOpen(true);
  };

  const openEdit = (type: FundCustomType) => {
    setEditing(type);
    setName(type.name);
    setDialogOpen(true);
  };

  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.patch<FundCustomType>(`/giving/fund-custom-types/${editing.id}`, {
            name: name.trim(),
          })
        : api.post<FundCustomType>('/giving/fund-custom-types', {
            name: name.trim(),
          }),
    onSuccess: async () => {
      toast.success(t('funds.customType.saved'));
      closeDialog();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['giving', 'fund-custom-types'] }),
        queryClient.invalidateQueries({ queryKey: ['giving', 'funds'] }),
      ]);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<FundTypeTableRow>[] = [
    {
      id: 'name',
      header: t('types.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'code',
      header: t('types.columns.code'),
      cell: (row) => row.code ?? '—',
    },
    {
      id: 'source',
      header: t('types.columns.source'),
      cell: (row) => (
        <SourceBadge
          source={row.source}
          builtInLabel={t('types.sourceBuiltIn')}
          customLabel={t('types.sourceCustom')}
        />
      ),
    },
    {
      id: 'funds',
      header: t('types.columns.funds'),
      cell: (row) => formatNumber(row.fundCount),
    },
    {
      id: 'given',
      header: t('types.columns.given'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    {
      id: 'gifts',
      header: t('types.columns.gifts'),
      cell: (row) => formatNumber(row.giftCount),
    },
  ];

  const loading = customTypes.isLoading || funds.isLoading || insights.isLoading;

  return (
    <div>
      <PageHeader
        title={t('types.title')}
        description={t('types.description')}
        tipId="giving.funds.typeCreate"
        headerActions={
          canManage ? <Button onClick={openCreate}>{t('types.create')}</Button> : null
        }
      />
      <GivingSubnav />

      <div className="space-y-4">
        <DateRangeFilter
          value={dateRange}
          onChange={setDateRange}
          labels={{
            today: tCommon('date.today'),
            yesterday: tCommon('date.yesterday'),
            thisWeek: tCommon('date.thisWeek'),
            lastWeek: tCommon('date.lastWeek'),
            thisMonth: tCommon('date.thisMonth'),
            lastMonth: tCommon('date.lastMonth'),
            thisQuarter: tCommon('date.thisQuarter'),
            thisYear: tCommon('date.thisYear'),
            lastYear: tCommon('date.lastYear'),
            allTime: tCommon('date.allTime'),
            custom: tCommon('date.custom'),
            from: t('funds.insights.dateFrom'),
            to: t('funds.insights.dateTo'),
          }}
          presets={INSIGHTS_DATE_RANGE_PRESETS}
        />

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => `${row.source}:${row.id}`}
          loading={loading}
          totals={dataTableMoneyColumnTotals('given', tableTotals, baseCurrency)}
          rowActions={
            canManage
              ? (row) =>
                  row.source === 'custom' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const type = customTypes.data?.data.find((item) => item.id === row.id);
                        if (type) openEdit(type);
                      }}
                    >
                      {tCommon('action.edit')}
                    </Button>
                  ) : null
              : undefined
          }
          actionsHeader={canManage ? tCommon('table.actions') : undefined}
        />
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(next) => {
          if (next) setDialogOpen(true);
          else closeDialog();
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>
              {editing ? t('funds.customType.editTitle') : t('funds.customType.createTitle')}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim().length < 2) return;
              save.mutate();
            }}
          >
            <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
              <FormField
                label={t('funds.customType.name')}
                tipId={editing ? undefined : 'giving.funds.typeName'}
                required
              >
                {({ id, ...field }) => (
                  <Input
                    id={id}
                    {...field}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                    autoFocus
                    maxLength={100}
                  />
                )}
              </FormField>
              {editing ? (
                <FormField label={t('funds.code')} hint={t('funds.customType.codeLocked')}>
                  {({ id, ...field }) => (
                    <Input id={id} {...field} value={editing.code} disabled />
                  )}
                </FormField>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={save.isPending} disabled={name.trim().length < 2}>
                {t('funds.customType.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
